import { useState, useEffect } from 'react'
import { CAMPAIGN_TYPE_OPTIONS } from '../constants/campaignTypes'
import { getDashboardOverview, DashboardFilters } from '../services/api'
import MetricCard from '../components/MetricCard'
import LoadingSpinner from '../components/LoadingSpinner'
import CostRevenueByLevel from '../components/CostRevenueByLevel'

interface DashboardData {
  summary: {
    totalLeads: number
    totalSpend: number
    totalRevenue: number
    profit: number
  }
  kpis: {
    roi: number
    cpl: number
    rpl: number
    profitMargin: number
    validationRate: number
  }
  channels: {
    google: { spend: number; percentage: number }
    microsoft: { spend: number; percentage: number }
    facebook: { spend: number; percentage: number }
    tiktok: { spend: number; percentage: number }
  }
  costRevenueByLevel: {
    producido: { leads: number; revenue: number; cpl: number; rpl: number }
    unico: { leads: number; revenue: number; cpl: number; rpl: number }
  }
  metadata: {
    dataSource: string
    totalRecords: number
    lastUpdated: string
    currency: string
  }
}

const Google = () => {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    startDate: '2025-09-01',
    endDate: '2025-11-30',
    source: 'google',
    type: 'all',
    flow: 'all'
  })

  const fetchData = async (appliedFilters?: DashboardFilters) => {
    try {
      setLoading(true)
      
      const filtersToUse = appliedFilters || {
        startDate: filters.startDate,
        endDate: filters.endDate,
        source: filters.source,
        type: filters.type,
        flow: filters.flow
      }
      
      const response = await getDashboardOverview(filtersToUse)
      setData(response.data)
      setError(null)
      
      console.log('🔍 Datos de Google cargados con filtros:', filtersToUse)
    } catch (err) {
      setError('Error al cargar los datos de Google')
      console.error('Error fetching Google data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    const newFilters = { ...filters, startDate, endDate }
    setFilters(newFilters)
    
    const apiFilters: DashboardFilters = {
      startDate,
      endDate,
      source: 'google',
      type: newFilters.type,
      flow: newFilters.flow
    }
    fetchData(apiFilters)
  }

  const handleSourceChange = (_source: string) => {
    // Google page always filters by Google
    const newFilters = { ...filters, source: 'google' }
    setFilters(newFilters)
  }

  const resetAllFilters = () => {
    const defaultFilters = {
      startDate: '2025-09-01',
      endDate: '2025-11-30',
      source: 'google',
      type: 'all',
      flow: 'all'
    }
    setFilters(defaultFilters)
    
    const apiFilters: DashboardFilters = {
      startDate: defaultFilters.startDate,
      endDate: defaultFilters.endDate,
      source: 'google',
      type: defaultFilters.type,
      flow: defaultFilters.flow
    }
    fetchData(apiFilters)
  }

  const handleTypeChange = (type: string) => {
    const newFilters = { ...filters, type }
    setFilters(newFilters)
    
    const apiFilters: DashboardFilters = {
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      source: 'google',
      type,
      flow: newFilters.flow
    }
    fetchData(apiFilters)
  }

  const handleFlowChange = (flow: string) => {
    const newFilters = { ...filters, flow }
    setFilters(newFilters)
    
    const apiFilters: DashboardFilters = {
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      source: 'google',
      type: newFilters.type,
      flow
    }
    fetchData(apiFilters)
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">⚠️</div>
          <p className="text-dashboard-muted">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary mt-4"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return <div>No hay datos disponibles</div>
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
            <span className="mr-3">🔍</span>
            Google Details
          </h1>
          <p className="text-dashboard-muted mt-1">
            Análisis específico del rendimiento de Google Ads
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-dashboard-muted">
            Última actualización
          </div>
          <div className="text-sm font-semibold text-dashboard-text">
            {new Date(data.metadata.lastUpdated).toLocaleString('es-ES')}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filter-container">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-dashboard-text">Filtros de Análisis</h3>
          <button 
            onClick={resetAllFilters}
            className="btn-secondary text-sm"
          >
            Resetear
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleDateRangeChange(e.target.value, filters.endDate)}
              className="filter-select w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleDateRangeChange(filters.startDate, e.target.value)}
              className="filter-select w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fuente
            </label>
            <select
              value={filters.source}
              onChange={(e) => handleSourceChange(e.target.value)}
              className="filter-select w-full"
            >
              <option value="google">Google</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Tipo
            </label>
            <select
              value={filters.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              className="filter-select w-full"
            >
              {CAMPAIGN_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Flow
            </label>
            <select
              value={filters.flow}
              onChange={(e) => handleFlowChange(e.target.value)}
              className="filter-select w-full"
            >
              <option value="all">Todos los flows</option>
              <option value="seguro-salud">Seguro Salud</option>
              <option value="creditos-personales">Créditos Personales</option>
              <option value="tarjeta-credito">Tarjeta Crédito</option>
              <option value="seguro-decesos">Seguro Decesos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Métricas Principales de Google */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Inversión Google"
          value={`€${data.channels.google.spend.toFixed(2)}`}
          icon="🔍"
          subtitle="Gasto en Google Ads"
          premium={true}
        />
        <MetricCard
          title="Leads Google"
          value={data.summary.totalLeads.toLocaleString()}
          icon="👥"
          subtitle="Leads generados"
        />
        <MetricCard
          title="Revenue Google"
          value={`€${data.summary.totalRevenue.toFixed(2)}`}
          icon="💎"
          subtitle="Ingresos generados"
        />
        <MetricCard
          title="ROI Google"
          value={`${data.kpis.roi.toFixed(1)}%`}
          icon="📈"
          trend={data.kpis.roi >= 0 ? "positive" : "negative"}
          subtitle="Return on Investment"
        />
      </div>

      {/* KPIs Específicos de Google */}
      <div>
        <h2 className="text-xl font-bold text-dashboard-text mb-4 flex items-center">
          <span className="mr-2">⚡</span>
          KPIs de Google Ads
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <MetricCard
            title="CPL"
            value={`€${data.kpis.cpl.toFixed(3)}`}
            icon="🎯"
            subtitle="Cost Per Lead"
          />
          <MetricCard
            title="RPL"
            value={`€${data.kpis.rpl.toFixed(3)}`}
            icon="💎"
            subtitle="Revenue Per Lead"
          />
          <MetricCard
            title="Profit Margin"
            value={`${data.kpis.profitMargin.toFixed(1)}%`}
            icon="📈"
            trend={data.kpis.profitMargin >= 0 ? "positive" : "negative"}
            subtitle="Margen de beneficio"
          />
          <MetricCard
            title="Validation Rate"
            value={`${(data.kpis.validationRate * 100).toFixed(1)}%`}
            icon="✅"
            subtitle="Tasa de validación"
          />
          <MetricCard
            title="Profit/Loss"
            value={`€${data.summary.profit.toFixed(2)}`}
            icon={data.summary.profit >= 0 ? "📈" : "📉"}
            trend={data.summary.profit >= 0 ? "positive" : "negative"}
            subtitle="Beneficio neto"
          />
        </div>
      </div>

      {/* Coste y Revenue por Nivel (H1 / Fase A #3) */}
      <CostRevenueByLevel data={data.costRevenueByLevel} />

      {/* Información del Canal */}
      <div className="card p-6 bg-gradient-to-r from-blue-50 to-white">
        <h2 className="text-lg font-bold text-dashboard-text mb-6 flex items-center">
          <span className="mr-2">🔍</span>
          Información del Canal Google
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">📊</span>
            </div>
            <p className="text-sm text-dashboard-muted mb-1">Participación</p>
            <p className="font-semibold text-dashboard-text">{data.channels.google.percentage.toFixed(1)}% del total</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">💰</span>
            </div>
            <p className="text-sm text-dashboard-muted mb-1">Inversión</p>
            <p className="font-semibold text-dashboard-text">€{data.channels.google.spend.toFixed(2)}</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">📈</span>
            </div>
            <p className="text-sm text-dashboard-muted mb-1">Performance</p>
            {/* Antes usaba la cuota de inversión (percentage > 25) como si
                fuera "rendimiento" — un canal que gasta mucho y pierde
                dinero salía como "Alto". Ahora se basa en el ROI real. */}
            <p className={`font-semibold ${data.kpis.roi >= 0 ? 'text-green-600' : data.kpis.roi >= -30 ? 'text-dashboard-text' : 'text-red-600'}`}>
              {data.kpis.roi >= 0 ? 'Alto' : data.kpis.roi >= -30 ? 'Medio' : 'Bajo'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Google
